import type { ReactNode } from 'react';
import { App, Button, Flex, Table, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { downloadBlob } from '@/shared/lib/downloadBlob';
import { translateApiError } from '@/shared/api/errorToMessage';

export interface ReportPageProps<T> {
  title: string;
  filters?: ReactNode;
  columns: ColumnsType<T>;
  data: T[];
  loading?: boolean;
  rowKey: string | ((record: T) => string);
  expandable?: TableProps<T>['expandable'];
  /** Доп. блок над таблицей (например, итоговая строка «Итого: N») */
  summary?: ReactNode;
  onExport: () => Promise<Blob>;
  exportFilename: string;
}

/** §13: общая обёртка для всех 9 отчётов — FilterBar → Table → Печать/Экспорт (§13). */
export function ReportPage<T>({ title, filters, columns, data, loading, rowKey, expandable, summary, onExport, exportFilename }: ReportPageProps<T>) {
  const { message } = App.useApp();

  async function handleExport() {
    try {
      const blob = await onExport();
      downloadBlob(blob, exportFilename);
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  return (
    <Flex vertical gap={12}>
      <Flex justify="space-between" align="center" wrap className="no-print">
        <Typography.Title level={5} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
        <Flex gap={8}>
          <Button onClick={() => window.print()}>Печать</Button>
          <Button onClick={() => void handleExport()}>Экспорт</Button>
        </Flex>
      </Flex>

      {filters ? (
        <div className="filter-bar no-print" style={{ overflow: 'visible' }}>
          {filters}
        </div>
      ) : null}

      {summary}

      <Table<T>
        size="small"
        rowKey={rowKey}
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={false}
        bordered
        expandable={expandable}
      />
    </Flex>
  );
}
